import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Download, RefreshCcw } from 'lucide-react';
import axios from '../../../axiosConfig';
import { useToast } from '../../../components/ui/Toast';
import { useCurrency } from '../../../contexts/CurrencyContext';
import './TaxSummaryTab.css';

/* ─── Module-scope helpers ─────────────────────────────────────── */

function todayIso() {
  return new Date().toISOString().slice(0, 10);
}

function firstOfMonthIso(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth(), 1).toISOString().slice(0, 10);
}

function firstOfYearIso(d = new Date()) {
  return new Date(d.getFullYear(), 0, 1).toISOString().slice(0, 10);
}

function startOfLastMonthIso(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth() - 1, 1).toISOString().slice(0, 10);
}

function endOfLastMonthIso(d = new Date()) {
  return new Date(d.getFullYear(), d.getMonth(), 0).toISOString().slice(0, 10);
}

const PRESETS = [
  { id: 'this_month', label: 'This month' },
  { id: 'last_month', label: 'Last month' },
  { id: 'ytd',        label: 'Year to date' },
  { id: 'custom',     label: 'Custom' },
];

const CHANNEL_LABEL = {
  STOREFRONT: 'Storefront',
  POS:        'POS',
  WHATSAPP:   'WhatsApp',
  MANUAL:     'Manual',
  INVOICE:    'Invoice',
  APPOINTMENT:'Appointment',
  UNKNOWN:    'Other',
};

/* ─── Module-scope sub-components ──────────────────────────────── */

function PeriodFilter({ preset, onPresetChange, from, to, onFromChange, onToChange, groupBy, onGroupByChange }) {
  return (
    <div className="fin-tax-filter">
      <div className="fin-tax-filter-row">
        <div className="fin-tax-presets" role="tablist" aria-label="Period">
          {PRESETS.map((p) => (
            <button
              key={p.id}
              type="button"
              role="tab"
              aria-selected={preset === p.id}
              className={`fin-tax-preset${preset === p.id ? ' is-active' : ''}`}
              onClick={() => onPresetChange(p.id)}
            >
              {p.label}
            </button>
          ))}
        </div>
        <div className="fin-tax-groupby" role="tablist" aria-label="Group by">
          {['day', 'week', 'month'].map((g) => (
            <button
              key={g}
              type="button"
              role="tab"
              aria-selected={groupBy === g}
              className={`fin-tax-gb${groupBy === g ? ' is-active' : ''}`}
              onClick={() => onGroupByChange(g)}
            >
              {g.charAt(0).toUpperCase() + g.slice(1)}
            </button>
          ))}
        </div>
      </div>
      {preset === 'custom' && (
        <div className="fin-tax-filter-row fin-tax-filter-row--dates">
          <label className="fin-tax-date">
            <span>From</span>
            <input type="date" value={from} onChange={(e) => onFromChange(e.target.value)} />
          </label>
          <label className="fin-tax-date">
            <span>To</span>
            <input type="date" value={to} onChange={(e) => onToChange(e.target.value)} />
          </label>
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, hint }) {
  return (
    <div className="fin-tax-stat">
      <div className="fin-tax-stat-label">{label}</div>
      <div className="fin-tax-stat-value">{value}</div>
      {hint && <div className="fin-tax-stat-hint">{hint}</div>}
    </div>
  );
}

function PeriodTable({ rows, fmtMoney }) {
  if (rows.length === 0) {
    return (
      <div className="fin-tax-empty">
        <div className="fin-tax-empty-title">No tax collected in this period</div>
        <div className="fin-tax-empty-sub">
          Either no paid taxable records fell in this range, or merchant tax is currently disabled.
        </div>
      </div>
    );
  }
  return (
    <div className="fin-tax-table-wrap">
      <table className="fin-tax-table">
        <colgroup>
          <col />
          <col style={{ width: 110 }} />
          <col style={{ width: 140 }} />
          <col style={{ width: 140 }} />
        </colgroup>
        <thead>
          <tr>
            <th>Period</th>
            <th className="fin-tax-th-num">Records</th>
            <th className="fin-tax-th-num">Subtotal</th>
            <th className="fin-tax-th-num">Tax collected</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.period}>
              <td className="fin-tax-td-label">{r.label}</td>
              <td className="fin-tax-td-num" data-label="Records">{r.orderCount}</td>
              <td className="fin-tax-td-num" data-label="Subtotal">{fmtMoney(r.subtotalSum)}</td>
              <td className="fin-tax-td-num fin-tax-td-strong" data-label="Tax">{fmtMoney(r.taxCollected)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function ChannelStrip({ rows, fmtMoney, totalTax }) {
  if (rows.length === 0) return null;
  return (
    <div className="fin-tax-channels">
      <div className="fin-tax-channels-title">By channel</div>
      <div className="fin-tax-channels-grid">
        {rows.map((r) => {
          const pct = totalTax > 0 ? Math.round((r.taxCollected / totalTax) * 100) : 0;
          return (
            <div className="fin-tax-channel" key={r.channel}>
              <div className="fin-tax-channel-head">
                <span className="fin-tax-channel-name">{CHANNEL_LABEL[r.channel] || r.channel}</span>
                <span className="fin-tax-channel-share">{pct}%</span>
              </div>
              <div className="fin-tax-channel-bar">
                <div className="fin-tax-channel-bar-fill" style={{ width: `${pct}%` }} />
              </div>
              <div className="fin-tax-channel-meta">
                {fmtMoney(r.taxCollected)} · {r.orderCount} record{r.orderCount === 1 ? '' : 's'}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

/* ─── Main component ──────────────────────────────────────────── */

export default function TaxSummaryTab() {
  const toast = useToast();
  const { formatMoney } = useCurrency();

  const [preset, setPreset] = useState('this_month');
  const [from, setFrom] = useState(firstOfMonthIso());
  const [to, setTo] = useState(todayIso());
  const [groupBy, setGroupBy] = useState('month');
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);

  // Adjust the range when the preset changes — keeps Custom inputs free.
  useEffect(() => {
    if (preset === 'this_month') {
      setFrom(firstOfMonthIso());
      setTo(todayIso());
    } else if (preset === 'last_month') {
      setFrom(startOfLastMonthIso());
      setTo(endOfLastMonthIso());
    } else if (preset === 'ytd') {
      setFrom(firstOfYearIso());
      setTo(todayIso());
    }
    // 'custom' leaves the existing values alone.
  }, [preset]);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get('/finance/tax-summary', {
        params: { from, to, groupBy }
      });
      setData(data);
    } catch (err) {
      const msg = err?.response?.data?.error || err?.message || 'Failed to load tax summary';
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  }, [from, to, groupBy, toast]);

  useEffect(() => { reload(); }, [reload]);

  const fmtMoney = useCallback((v) => formatMoney(v ?? 0), [formatMoney]);
  const totals = data?.totals;
  const byPeriod = data?.byPeriod || [];
  const byChannel = data?.byChannel || [];

  const handleCsv = useCallback(async () => {
    try {
      const resp = await axios.get('/finance/tax-summary.csv', {
        params: { from, to }, responseType: 'blob',
      });
      const blob = new Blob([resp.data], { type: 'text/csv;charset=utf-8' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tax-summary-${from}-to-${to}.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Failed to download CSV');
    }
  }, [from, to, toast]);

  return (
    <div className="fin-tax-tab">
      <header className="fin-tax-header">
        <div className="fin-tax-header-text">
          <h2 className="fin-tax-title">Tax summary</h2>
          <p className="fin-tax-subtitle">
            Tax recorded on paid orders, merchant invoices, and appointment payments. Hand the CSV to your accountant or use it for filing.
          </p>
        </div>
        <div className="fin-tax-header-actions">
          <button
            type="button"
            className="fin-tax-btn fin-tax-btn--ghost"
            onClick={reload}
            disabled={loading}
          >
            <RefreshCcw size={14} />
            {loading ? 'Loading…' : 'Refresh'}
          </button>
          <button
            type="button"
            className="fin-tax-btn fin-tax-btn--primary"
            onClick={handleCsv}
            disabled={loading || !totals || totals.orderCount === 0}
          >
            <Download size={14} />
            Export CSV
          </button>
        </div>
      </header>

      <PeriodFilter
        preset={preset}
        onPresetChange={setPreset}
        from={from}
        to={to}
        onFromChange={(v) => { setPreset('custom'); setFrom(v); }}
        onToChange={(v) => { setPreset('custom'); setTo(v); }}
        groupBy={groupBy}
        onGroupByChange={setGroupBy}
      />

      <section className="fin-tax-stats">
        <StatCard
          label="Tax collected"
          value={loading ? '—' : fmtMoney(totals?.taxCollected)}
          hint={loading ? null : `${totals?.orderCount || 0} paid records`}
        />
        <StatCard
          label="Average per record"
          value={loading ? '—' : fmtMoney(totals?.avgPerOrder)}
        />
        <StatCard
          label="Taxable subtotal"
          value={loading ? '—' : fmtMoney(totals?.subtotalSum)}
          hint="Pre-tax sales total"
        />
      </section>

      <PeriodTable rows={byPeriod} fmtMoney={fmtMoney} />
      {byChannel.some((r) => r.channel === 'POS') && (
        <div className="fin-tax-note">
          <strong>Heads up:</strong> POS tax only counts orders marked
          <em> paid</em>. Bank-transfer sales sit unpaid until you confirm
          payment. If POS tax looks low here,{' '}
          <a href="/orders">check your Orders page</a> for pending POS
          orders waiting to be confirmed.
        </div>
      )}
      <ChannelStrip rows={byChannel} fmtMoney={fmtMoney} totalTax={totals?.taxCollected || 0} />
    </div>
  );
}
